import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostBinding,
  inject,
  Input,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { isNil, noop } from 'lodash';

import { UpgradeTagComponent } from '@shared/upgrade';

type OnChangeFunction = (value: number) => void;
type OnTouchFunction = () => void;

@Component({
  selector: 'la-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatSliderModule, MatFormFieldModule, MatInputModule, UpgradeTagComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderComponent),
      multi: true,
    },
  ],
})
export class SliderComponent implements ControlValueAccessor {
  private cd = inject(ChangeDetectorRef);

  @Input() min?: number;
  @Input() max?: number;
  @Input() step = 1;
  @Input() label = '';
  @Input() disabled = false;
  @Input() suffix?: string;
  @Input() showUpgradeTagWhenDisabled = false;
  @Input({ transform: booleanAttribute }) compact = false;

  @HostBinding('class.compact')
  get isCompact() {
    return this.compact;
  }

  sliderValue?: number;

  onChange: OnChangeFunction = noop;
  onTouch: OnTouchFunction = noop;

  registerOnChange(fn: OnChangeFunction): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchFunction): void {
    this.onTouch = fn;
  }

  writeValue(input: number) {
    this.sliderValue = Math.round(+input);
    this.cd.markForCheck();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  inputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = +target.value;
    if (!isNil(value)) {
      this.passValue(value);
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    const forbiddenKeys = ['.', ',', '-', '+', 'e', 'E'];
    if (forbiddenKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;

    if (target.value === '') {
      const fallbackValue = this.min ?? 0;
      this.passValue(fallbackValue);
      target.value = String(fallbackValue);
      return;
    }

    let value = parseInt(target.value, 10);

    if (!isNaN(value)) {
      value = Math.round(value);
      value = this.sanitizeValue(value);

      target.value = String(value);

      this.passValue(value);
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pastedData = event.clipboardData?.getData('text');
    if (pastedData && !/^\d+$/.test(pastedData)) {
      event.preventDefault();
    }
  }

  passValue(value: number) {
    value = Math.round(value);
    value = this.sanitizeValue(value);
    this.sliderValue = value;
    this.onChange(this.sliderValue);
    this.cd.markForCheck();
  }

  private sanitizeValue(value: number): number {
    if (this.min != null && value < this.min) return this.min;
    if (this.max != null && value > this.max) return this.max;
    return value;
  }
}
